import * as Haptics from 'expo-haptics';
import ClearIcon from '@assets/icons/clear.svg';
import Constants from 'expo-constants';
import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { APP_ROUTES, CHECK_IN_ROUTES, ORDER_ROUTES } from '@/navigators/routes';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import { CheckInFilters, CheckInItem } from '@/contexts/check-in-context-types';
import { computeProgressPercent } from '@/utils/order-check-in';
import { createStyles } from '@/contexts/theme-context';
import {
  FONTS,
  SCREEN_PADDING,
  SCREEN_PADDING_SMALL,
  TYPOGRAPHY,
} from '@/styles';
import { HappyBox } from '@/components/happy-box';
import {
  KeyboardAwareRecyclerList,
  ListSection,
} from '@/components/recycler-list';
import { ListEmptyState } from '@/components/list-empty-state';
import { Props as NavigationHeaderContentProps } from '@/components/navigation-header-content';
import { OrderCheckInFilter } from '@/components/order-check-in/order-check-in-filter';
import { OrderCheckInHandleWithProgress } from '@/components/order-check-in/order-check-in-handle-with-progress';
import { OrderCheckInListItem } from './order-check-in-list-item';
import { OrderCheckInSingleItemComponent } from './order-check-in-single-item-component';
import { OrderCheckInSummary } from '@/components/order-check-in/order-check-in-summary';
import { OrdersStackParams } from '@/navigators/types';
import { PillButtonType } from '@/components/pill-button';
import { ScannerType, ScanSound } from '@/components/scanner/scanner-types';
import { ScanPageContainer } from '@/components/scanner/scan-page-container';
import { StackNavigationProp } from '@react-navigation/stack';
import { Text, TouchableOpacity, View } from 'react-native';
import { useCheckInContext } from '@/contexts/check-in-context';
import {
  useLayoutContext,
  useSmallLayoutSizeValue,
} from '@/contexts/layout-context';
import { useNavigation } from '@react-navigation/native';
import { useToasts } from '@/contexts/toast-context';

const hasSocketMobileSupport = Boolean(
  Constants.expoConfig!.extra?.socketMobile,
);

interface Props {
  checkInCompleted: boolean;
  invoiceUUID: string;
  orderUUID?: string;
}

export const OrderCheckInComponent: React.FC<Props> = ({
  checkInCompleted,
  invoiceUUID,
  orderUUID,
}) => {
  const [isCheckInSummaryVisible, setIsCheckInSummaryVisible] = useState(false);
  const [isCheckInSuccessVisible, setIsCheckInSuccessVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCheckInItem, setSelectedCheckInItem] =
    useState<CheckInItem | null>(null);
  const savingRef = useRef(false);

  const [styles] = useStyles();
  const { addListener, navigate, goBack, dispatch } =
    useNavigation<StackNavigationProp<OrdersStackParams>>();
  const { isSmallLayout } = useLayoutContext();
  const {
    bottomActionWrapperStyle,
    centerWrapperStyle,
    checkInlistItemStyle,
    fixedWidthStyle,
  } = useSmallLayoutSizeValue(
    {
      bottomActionWrapperStyle: styles.checkInBottomWrapper,
      checkInlistItemStyle: styles.checkInlistItem,
    },
    {
      bottomActionWrapperStyle: styles.checkInBottomWrapperTablet,
      centerWrapperStyle: styles.centerWrapper,
      checkInlistItemStyle: styles.checkInlistItemTablet,
      fixedWidthStyle: styles.fixedWidth,
    },
  );

  const {
    adaptCheckInItemFragment,
    autoIncrementQuantity,
    clearSearchResults,
    completeCheckIn,
    filteredCheckInItems,
    filteredIdsMap,
    itemQuantityMap,
    loading,
    onItemQuantityUpdate,
    playScanSound,
    refetchCheckInQuery,
    saveCheckInChanges,
    searchForItems,
    searchResults,
    selectedFilter,
    setSelectedFilter,
  } = useCheckInContext();
  const { showErrorToast } = useToasts();

  const checkInItemsData = useMemo(() => {
    if (searchTerm.length > 0) {
      return searchResults ?? [];
    }
    return filteredCheckInItems ?? [];
  }, [filteredCheckInItems, searchResults, searchTerm.length]);

  const onClear = () => {
    setSearchTerm('');
    clearSearchResults();
  };

  const onDonePress = async () => {
    await saveCheckInChanges();
    onClear();
    if (selectedFilter !== CheckInFilters.PARTIAL_OR_MISSING) {
      setSelectedFilter(CheckInFilters.PARTIAL_OR_MISSING);
    }
  };

  const onSearch = async (code: string, scannerType: ScannerType) => {
    setSearchTerm(code);

    const checkInSearchResults = searchForItems(code, scannerType);

    if (checkInSearchResults.length === 1) {
      const searchTermLowerCase = code.toLowerCase();
      const vp =
        checkInSearchResults[0].order_invoice_line_item.variant_pack ?? null;
      const externalItemId = (vp?.external_item_id ?? '').toLowerCase();
      const upcList: string[] = vp?.upc_list ?? [];

      if (
        externalItemId === searchTermLowerCase ||
        upcList.some((upc) => upc.toLowerCase() === searchTermLowerCase)
      ) {
        const checkInItem = adaptCheckInItemFragment(checkInSearchResults[0]);

        if (scannerType === ScannerType.External && !hasSocketMobileSupport) {
          navigate(CHECK_IN_ROUTES.CheckInSingleItem, {
            checkInItem,
            onDonePress,
          });
        } else {
          autoIncrementQuantity(checkInItem);
          setSelectedCheckInItem(checkInItem);
        }
      }
    }

    if (checkInSearchResults && checkInSearchResults.length === 0) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
      playScanSound(ScanSound.NoResults);
    } else if (checkInSearchResults && checkInSearchResults.length > 1) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      playScanSound(ScanSound.MultipleResults);
    }
  };

  const filterPills: PillButtonType[] = useMemo(
    () => [
      {
        active: selectedFilter === CheckInFilters.PARTIAL_OR_MISSING,
        count: filteredIdsMap.PARTIAL_OR_MISSING.size,
        label: i18n.t('check_in.filter.partial_or_missing'),
        onPress: () => setSelectedFilter(CheckInFilters.PARTIAL_OR_MISSING),
      },
      {
        active: selectedFilter === CheckInFilters.FULLY_RECEIVED,
        count: filteredIdsMap.FULLY_RECEIVED.size,
        label: i18n.t('check_in.filter.fully_received'),
        onPress: () => setSelectedFilter(CheckInFilters.FULLY_RECEIVED),
      },
      {
        active: selectedFilter === CheckInFilters.ALL,
        count: filteredIdsMap.ALL.size,
        label: i18n.t('all'),
        onPress: () => setSelectedFilter(CheckInFilters.ALL),
      },
      {
        active: selectedFilter === CheckInFilters.HAS_ISSUES,
        count: filteredIdsMap.HAS_ISSUES.size,
        label: i18n.t('check_in.filter.has_issues'),
        onPress: () => setSelectedFilter(CheckInFilters.HAS_ISSUES),
      },
      {
        active: selectedFilter === CheckInFilters.NOT_FULFILLED,
        count: filteredIdsMap.NOT_FULFILLED.size,
        label: i18n.t('check_in.filter.not_fulfilled'),
        onPress: () => setSelectedFilter(CheckInFilters.NOT_FULFILLED),
      },
    ],
    [filteredIdsMap, selectedFilter, setSelectedFilter],
  );

  useEffect(() => {
    // Intercept any attempt to remove this screen (back press, gesture, Android back)
    const unsubscribe = addListener('beforeRemove', async (e) => {
      if (savingRef.current) {
        return;
      }
      savingRef.current = true;

      dispatch(e.data.action);

      try {
        await saveCheckInChanges();
      } finally {
        unsubscribe(); // avoid re-entering
        savingRef.current = false;
      }
    });

    return unsubscribe;
  }, [addListener, dispatch, saveCheckInChanges, showErrorToast]);

  const navigateBack = isSmallLayout
    ? goBack
    : async () => {
        navigate(APP_ROUTES.Orders, {
          params: {
            invoiceUUID,
            orderUUID,
          },
          screen: ORDER_ROUTES.Orders,
        });
        await saveCheckInChanges();
      };

  const navigationHeaderProps: NavigationHeaderContentProps = {
    onBackPress: navigateBack,
  };

  const checkInItems = useMemo<ListSection<CheckInItem>[]>(() => {
    return [
      {
        data: checkInItemsData.map(adaptCheckInItemFragment),
        key: null,
      },
    ];
  }, [adaptCheckInItemFragment, checkInItemsData]);

  const noResultsText = useMemo(() => {
    if (searchTerm.length > 0) {
      return i18n.t('no_results_for', {
        searchTerm,
      });
    } else if (selectedFilter === CheckInFilters.PARTIAL_OR_MISSING) {
      return i18n.t('check_in.all_received');
    }
    return i18n.t('no_items_in_this_category');
  }, [searchTerm, selectedFilter]);

  const listEmptyComponent = (
    <View style={styles.emptyListState}>
      <ListEmptyState text={noResultsText} />
    </View>
  );

  const onCompleteCheckInPress = useCallback(async () => {
    await saveCheckInChanges();
    try {
      const { data } = await refetchCheckInQuery();
      if (data) {
        const updatedShippedItems =
          data?.order_invoice_check_ins_by_pk?.order_invoice_check_in_items.filter(
            (item) => item.order_invoice_line_item.ship_quantity >= 0,
          ) ?? [];
        const someHaveIssues = updatedShippedItems.some(
          (item) => item.order_invoice_check_in_item_issues.length > 0,
        );
        const someAreMissing = updatedShippedItems.some(
          (item) => item.quantity < item.order_invoice_line_item.ship_quantity,
        );

        if (someHaveIssues || someAreMissing) {
          setIsCheckInSummaryVisible(true);
        } else {
          try {
            await completeCheckIn();
            setIsCheckInSuccessVisible(true);
          } catch {
            showErrorToast();
          }
        }
      }
    } catch {
      showErrorToast();
    }
  }, [
    completeCheckIn,
    refetchCheckInQuery,
    saveCheckInChanges,
    showErrorToast,
  ]);

  const onCheckInSuccessClose = () => {
    setIsCheckInSuccessVisible(false);
    navigateBack();
  };

  const onOrderCheckInSummaryClose = (shouldNavigateBack: boolean) => {
    setIsCheckInSummaryVisible(false);
    if (shouldNavigateBack) {
      navigateBack();
    }
  };

  const onScannerTypeChange = () => {
    setSelectedCheckInItem(null);
    setSearchTerm('');
  };

  const progress = useMemo(() => {
    if (!selectedCheckInItem) {
      return 0;
    }
    return computeProgressPercent(
      itemQuantityMap[selectedCheckInItem.checkInItemUUID],
      selectedCheckInItem.shipQuantity,
    );
  }, [itemQuantityMap, selectedCheckInItem]);

  return (
    <ScanPageContainer
      bottomAction={
        selectedCheckInItem ? null : (
          <View style={[bottomActionWrapperStyle, centerWrapperStyle]}>
            <Button
              onPress={onCompleteCheckInPress}
              style={fixedWidthStyle}
              trackingData={{
                name: checkInCompleted
                  ? 'update_check_in_button'
                  : 'complete_check_in_button',
                properties: {
                  invoiceUUID,
                },
              }}
            >
              {checkInCompleted
                ? i18n.t('check_in.button.update')
                : i18n.t('check_in.button.complete')}
            </Button>
          </View>
        )
      }
      bottomSheetProps={
        selectedCheckInItem
          ? {
              handleComponent: () => (
                <OrderCheckInHandleWithProgress
                  hasIssues={(selectedCheckInItem.issues ?? []).length > 0}
                  progress={progress}
                />
              ),
            }
          : undefined
      }
      navigationHeaderProps={navigationHeaderProps}
      onClearResults={onClear}
      onScannerTypeChange={onScannerTypeChange}
      scanInputProps={{
        bottomContainerStyle: styles.errorContainer,
        containerStyle: styles.searchContainer,
        scanInputClearable: true,
      }}
      searchByCode={onSearch}
    >
      {(scannerType) => (
        <>
          {loading ? (
            <View style={styles.staticContentContainer}>
              <ActivityIndicator testID="loading-indicator" />
            </View>
          ) : selectedCheckInItem && scannerType === ScannerType.Camera ? (
            <View style={styles.checkInCameraContainer}>
              <OrderCheckInSingleItemComponent
                checkInItem={selectedCheckInItem}
                containerStyle={styles.checkInSingleItemContainer}
                hideProgressBar
                onItemQuantityUpdate={onItemQuantityUpdate}
                quantity={itemQuantityMap[selectedCheckInItem.checkInItemUUID]}
              />
              <TouchableOpacity
                onPress={() => {
                  setSelectedCheckInItem(null);
                  onClear();
                }}
                style={styles.clearIcon}
              >
                <ClearIcon />
              </TouchableOpacity>
            </View>
          ) : checkInItemsData ? (
            <View style={[styles.flexContainer, fixedWidthStyle]}>
              {searchTerm.length === 0 ? (
                <View
                  style={[
                    styles.filterContainer,
                    scannerType === ScannerType.Camera
                      ? styles.filterContainerCamera
                      : null,
                  ]}
                >
                  <OrderCheckInFilter filterPills={filterPills} />
                </View>
              ) : null}
              <View style={[styles.flexContainer, centerWrapperStyle]}>
                <View style={[styles.flexContainer, fixedWidthStyle]}>
                  <KeyboardAwareRecyclerList
                    alwaysBounceVertical={false}
                    estimatedItemSize={136}
                    extraData={itemQuantityMap}
                    keyboardDismissMode="on-drag"
                    keyboardShouldPersistTaps="handled"
                    ListEmptyComponent={listEmptyComponent}
                    listItemProps={{
                      listItemStyle: checkInlistItemStyle,
                      renderMainContent: (option) => (
                        <OrderCheckInListItem
                          checkInItem={option}
                          onItemQuantityUpdate={onItemQuantityUpdate}
                          quantity={itemQuantityMap[option.checkInItemUUID]}
                          saveCheckInChanges={saveCheckInChanges}
                          selectedFilter={selectedFilter}
                        />
                      ),
                    }}
                    sections={checkInItems}
                    shouldRenderSeparator={() => false}
                  />
                </View>
              </View>
            </View>
          ) : null}
          <OrderCheckInSummary
            invoiceUUID={invoiceUUID}
            isVisible={isCheckInSummaryVisible}
            onClose={onOrderCheckInSummaryClose}
          />
          <BaseActionSheet
            isVisible={isCheckInSuccessVisible}
            onClose={onCheckInSuccessClose}
          >
            <View style={styles.checkInSuccessWrapper}>
              <HappyBox />
              <Text style={styles.checkInSuccessTitle}>
                {i18n.t('check_in.confirmation.title')}
              </Text>
              <Text style={TYPOGRAPHY.p}>
                {i18n.t('check_in.confirmation.subtitle')}
              </Text>
            </View>
          </BaseActionSheet>
        </>
      )}
    </ScanPageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  centerWrapper: {
    alignItems: 'center',
  },
  checkInBottomWrapper: {
    backgroundColor: theme.sk.color.bg.primary.default,
    paddingHorizontal: SCREEN_PADDING_SMALL,
    paddingTop: SCREEN_PADDING_SMALL,
  },
  checkInBottomWrapperTablet: {
    backgroundColor: theme.sk.color.bg.primary.default,
    paddingVertical: SCREEN_PADDING,
  },
  checkInCameraContainer: {
    gap: 8,
  },
  checkInlistItem: {
    paddingHorizontal: SCREEN_PADDING_SMALL,
    paddingVertical: 8,
  },
  checkInlistItemTablet: {
    paddingHorizontal: 0,
    paddingTop: SCREEN_PADDING_SMALL,
    paddingVertical: 0,
  },
  checkInSingleItemContainer: {
    paddingTop: 8,
  },
  checkInSuccessTitle: {
    ...FONTS.primarySemiBold,
    fontSize: theme.fontSizes.large,
    marginBottom: 8,
  },
  checkInSuccessWrapper: {
    alignItems: 'center',
  },
  clearIcon: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.tertiary.hover,
    borderRadius: 32,
    height: 32,
    justifyContent: 'center',
    position: 'absolute',
    right: SCREEN_PADDING_SMALL,
    top: SCREEN_PADDING_SMALL,
    width: 32,
  },
  emptyListState: {
    paddingTop: 80,
  },
  errorContainer: {
    height: 0,
  },
  filterContainer: {
    alignItems: 'center',
    paddingBottom: SCREEN_PADDING_SMALL,
    paddingHorizontal: SCREEN_PADDING_SMALL,
  },
  filterContainerCamera: {
    paddingTop: 8,
  },
  fixedWidth: {
    marginHorizontal: 'auto',
    width: 480,
  },
  flexContainer: {
    flex: 1,
  },
  searchContainer: {
    flex: 1,
    marginBottom: SCREEN_PADDING_SMALL,
  },
  staticContentContainer: {
    flex: 1,
    justifyContent: 'center',
  },
}));
